// The sidebar's badges: how many things wait behind each item. Read from
// the shared attention counts (features/attention), so the sidebar, Home
// and the screens show the same numbers; nothing is fetched here.
//
// Tones come from features/attention/tones (one tone per kind, shared
// with Home and the screens): overdue todos are warning; meetings, tasks
// and review queues (skills) are grey counts. Deployments use deployAttention(), the wording and tone every
// surface shares: failing is danger, a check that never finished is only
// a warning ("not verified", never "down").

import { appsAttention, deployAttention } from "@/features/attention/predicates";
import { attentionTone, type AttentionTone } from "@/features/attention/tones";
import { useAttentionCounts } from "@/features/attention/useAttentionCounts";
import { pluralize } from "@/lib/format";

import { tasksRowCount, type NavCountKey } from "./navModel";

export type NavCountTone = AttentionTone;

export interface NavCount {
  value: number;
  tone: NavCountTone;
  /** Read after the item's name by screen readers: "Meetings, 12 need you". */
  label: string;
}

export type NavCounts = Partial<Record<NavCountKey, NavCount>>;

export function useNavCounts(customerId: string | null): NavCounts {
  const a = useAttentionCounts(customerId);
  const out: NavCounts = {};
  // Zero is never shown. A count still on its first load reads 0, so it
  // appears once its data has arrived.
  const put = (key: NavCountKey, value: number, tone: NavCountTone, label: string) => {
    if (value > 0) out[key] = { value, tone, label };
  };

  const m = a.meetings.waitingOnYou;
  put("meetings", m, attentionTone("meetings"), `${pluralize(m, "meeting")} ${m === 1 ? "needs" : "need"} you`);
  const todos = a.todos.enabled ? { due: a.todos.dueToday + a.todos.overdue, overdue: a.todos.overdue } : null;
  if (todos) {
    // The Todos row's count.
    put(
      "todos",
      todos.due,
      attentionTone("todos", { overdue: todos.overdue }),
      todos.overdue > 0 ? `${pluralize(todos.due, "todo")} due, ${todos.overdue} overdue` : `${pluralize(todos.due, "todo")} due today`,
    );
  }
  // The Tasks row's: tasks that need you (todos have their own row and count).
  const row = tasksRowCount(null, a.tasks.enabled ? a.tasks.needsYou : null);
  if (row) put("tasks", row.value, row.tone, row.label);
  put("skills", a.skills.pending, attentionTone("skills"), `${pluralize(a.skills.pending, "skill")} to review`);
  // Where Apps is on: what waits on you in Code › Apps (apps/summary), else the legacy deploys.
  const deploys = a.apps ? appsAttention(a.apps) : deployAttention(a.deployments);
  if (deploys) put("code", deploys.count, deploys.tone, deploys.label);
  return out;
}
